import { useAction } from 'next-safe-action/hooks'
import { useCallback, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { toast } from 'react-toastify'

import {
  verifyAccount as verifyAccountAction,
  TAccountVerificationResponse,
} from '@/entities/account'
import { useServerErrorHandler } from '@/shared/config'

import { ACCOUNT_TOAST_CONTAINER_ID } from '../lib/toast'
import { TAccountFormValues } from '../model/form'

export const ERROR = {
  NOT_VERIFIED: 'NOT_VERIFIED',
  FAILED: 'FAILED',
  NOT_CHECKED: 'NOT_CHECKED',
}

export const useVerifyAccount = () => {
  const [isVerified, setIsVerified] = useState(false)
  const [isFailed, setIsFailed] = useState(false)

  const { setError, setValue } = useFormContext<TAccountFormValues>()
  const { handleServerError } = useServerErrorHandler()

  const handleNotVerifiedAccount = () => {
    setError('root.accountServerError', {
      type: ERROR.NOT_VERIFIED,
      message: '입력하신 계좌 정보가 유효하지 않습니다. 다시 확인해 주세요. ',
    })
    setError('bankName', { type: ERROR.NOT_VERIFIED })
    setError(
      'bankAccount',
      { type: ERROR.NOT_VERIFIED },
      {
        shouldFocus: true,
      },
    )
  }

  const handleAccountVerificationFailed = useCallback(() => {
    setIsFailed(true)
    setError('root.accountServerError', {
      type: ERROR.FAILED,
      message:
        '일시적으로 예금주 확인이 어렵습니다. 예금주명을 직접 입력해 주세요.',
    })
    setError(
      'accountHolder',
      { type: ERROR.FAILED },
      {
        shouldFocus: true,
      },
    )
    toast(`예금주명을 직접 입력해 주세요.`, {
      containerId: ACCOUNT_TOAST_CONTAINER_ID,
    })
  }, [setError])

  const handleVerifiedAccount = (data: TAccountVerificationResponse) => {
    setIsVerified(true)
    setValue('accountHolder', data.accountHolderName!)
    toast(`예금주명 “${data.accountHolderName}”\n확인이 완료되었습니다.`, {
      containerId: ACCOUNT_TOAST_CONTAINER_ID,
    })
  }

  const handleSuccess = (data: TAccountVerificationResponse) => {
    if (!data.success) {
      handleAccountVerificationFailed()

      return
    }

    if (!data.verified) {
      handleNotVerifiedAccount()

      return
    }

    handleVerifiedAccount(data)
  }

  const action = useAction(verifyAccountAction, {
    onSuccess: ({ data }) => {
      if (data) {
        handleSuccess(data)
      }
    },
    onError: ({ error }) => {
      if (error.serverError) {
        handleServerError(error.serverError)
      }
    },
  })

  const markAsUnverified = useCallback(() => {
    setIsVerified(false)
    setIsFailed(false)
  }, [])

  return {
    isVerified,
    isFailed,
    action,
    markAsUnverified,
    handleAccountVerificationFailed,
  }
}
